/**
 * Header — the sticky top bar of the AppShell.
 * Renders (left → right):
 *   1. Workspace badge
 *   2. Branch switcher
 *   3. QR Check-in (conditional)
 *   4. Role indicator / switcher
 *   5. Notification / push toggle (via NotificationBell)
 *   6. Mobile profile avatar
 * Mobile nav strip renders below.
 */
import { NavLink } from "react-router-dom";
import { useMemo } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Buildings, CaretDown, Circle, Check, QrCode, Camera, SignOut } from "@phosphor-icons/react";
import { ROLE_LABELS, ADDITIVE_QR_ROLES, initials } from "@/lib/helpers";
import { cn } from "@/lib/utils";
import { FileImage } from "@/components/ui/file-image";
import TrialBanner from "@/components/TrialBanner";
import NotificationBell from "@/components/shell/NotificationBell";
import { useNavigate } from "react-router-dom";

export default function Header({
  brand, org, user, roles, activeRole, activeBranch, branches,
  branchLabel, branchHasQr, flatNav,
  onOpenProfile, onLogout, setActiveRole, setActiveBranch,
}) {
  // Memoised so the dropdown doesn't re-filter every render — Header
  // re-renders on every route change and any AuthContext update.
  const visibleBranches = useMemo(
    () => branches.filter(b => roles.includes("owner") || user?.branch_ids?.includes(b.id)),
    [branches, roles, user?.branch_ids],
  );
  const navigate = useNavigate();
  // iter-58 — QR access (`display_qr`, `scan_qr`) is a permission capability,
  // NOT a primary role. It must never appear in the role selector, the role
  // badge, or any role-change dropdown. `primaryRoles` is what we render;
  // the underlying `roles` array (still carrying the QR keys) is used ONLY
  // to gate the top-bar Show/Scan QR buttons + backend permission checks.
  const primaryRoles = useMemo(
    () => (roles || []).filter(r => !ADDITIVE_QR_ROLES.includes(r)),
    [roles],
  );
  const primaryActiveRole = ADDITIVE_QR_ROLES.includes(activeRole)
    ? (primaryRoles[0] || activeRole)
    : activeRole;
  return (
    <header className="sticky top-0 z-40 bg-card/95 backdrop-blur border-b border-border">
      <TrialBanner />
      <div
        className="flex items-center gap-2 md:gap-3 px-4 lg:px-8 py-3 overflow-x-auto md:overflow-x-visible md:justify-between scrollbar-thin"
        style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "thin" }}
        data-testid="header-scroll-row"
      >
        {/* 1. Workspace name */}
        {org?.name ? (
          <div
            className="flex-none inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary px-2.5 py-1 text-[11px] font-medium max-w-[160px] sm:max-w-[220px]"
            title={`Workspace: ${org.name}`}
            data-testid="workspace-badge"
          >
            <Buildings size={12} weight="fill" />
            <span className="truncate">{org.name}</span>
          </div>
        ) : (
          <div className="lg:hidden font-display font-bold text-lg truncate max-w-[130px] flex-none">{brand.brand_name}</div>
        )}

        {/* 2. Branch switcher */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="branch-switcher" className="gap-2 flex-none">
              <Buildings size={16} weight="duotone" />
              <span className="max-w-[120px] truncate">{branchLabel}</span>
              <CaretDown size={12} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56 bg-card">
            <DropdownMenuLabel className="overline">Switch Branch</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {roles.includes("owner") && (
              <DropdownMenuItem onClick={() => setActiveBranch("all")} data-testid="branch-all">
                <Circle size={12} className="mr-2" /> All Branches
                {activeBranch === "all" && <Check size={14} className="ml-auto" />}
              </DropdownMenuItem>
            )}
            {visibleBranches.map(b => (
                <DropdownMenuItem key={b.id} onClick={() => setActiveBranch(b.id)} data-testid={`branch-${b.code}`}>
                  <Buildings size={14} className="mr-2 opacity-70" />
                  <span className="truncate">{b.name}</span>
                  {activeBranch === b.id && <Check size={14} className="ml-auto" />}
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* 3. iter-59/60 — Show QR / Scan QR (dedicated buttons).
             The old combined "Check-in" popup was removed — QR is a
             permission capability, so each button appears independently:
               • Show QR — visible when `display_qr` AND branch is on QR.
               • Scan QR — visible when `scan_qr` AND branch is on QR.
             Manual / GPS attendance is unaffected (Attendance page). */}
        {branchHasQr && roles.includes("display_qr") && (
          <QRTopBarButton
            testId="qr-display-btn"
            onClick={() => navigate("/qr/display")}
            icon={<QrCode size={16} weight="duotone" />}
            label="Show QR"
            disabled={activeBranch === "all"}
            title={activeBranch === "all"
              ? "Pick a specific branch first"
              : "Show QR for the active branch"}
          />
        )}
        {branchHasQr && roles.includes("scan_qr") && (
          <QRTopBarButton
            testId="qr-scan-btn"
            onClick={() => navigate("/qr/scan")}
            icon={<Camera size={16} weight="duotone" />}
            label="Scan QR"
            disabled={activeBranch === "all"}
            title={activeBranch === "all"
              ? "Pick a specific branch first"
              : "Scan a branch QR to check in / out"}
          />
        )}

        {/* 4. Role — QR capabilities are filtered out so they never appear
             as a switchable role or role badge. */}
        {primaryRoles.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="role-switcher" className="gap-2 flex-none">
                <Badge variant="outline" className="border-primary/30 bg-primary/5 text-primary font-medium">
                  {ROLE_LABELS[primaryActiveRole] || primaryActiveRole}
                </Badge>
                <CaretDown size={12} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52 bg-card">
              <DropdownMenuLabel className="overline">Switch Role</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {primaryRoles.map(r => (
                <DropdownMenuItem key={r} onClick={() => setActiveRole(r)} data-testid={`role-${r}`}>
                  {ROLE_LABELS[r] || r}
                  {primaryActiveRole === r && <Check size={14} className="ml-auto" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Badge variant="outline" className="flex-none border-primary/30 bg-primary/5 text-primary font-medium">
            {ROLE_LABELS[primaryActiveRole] || primaryActiveRole}
          </Badge>
        )}

        {/* Push desktop items to the right on md+ */}
        <div className="hidden md:block flex-1" />

        {/* 5. Notifications */}
        <NotificationBell />

        {/* 6. Mobile profile avatar */}
        <button
          type="button"
          onClick={onOpenProfile}
          className="lg:hidden rounded-full flex-none"
          aria-label="Open profile"
          data-testid="profile-open-btn-mobile"
        >
          <Avatar className="h-8 w-8">
            {user?.photo_file_id ? (
              <FileImage
                fileId={user.photo_file_id}
                alt=""
                className="aspect-square h-full w-full object-cover"
                fallback={
                  <AvatarFallback className="bg-primary/15 text-primary font-semibold text-xs">
                    {initials(user?.name || user?.email)}
                  </AvatarFallback>
                }
              />
            ) : (
              <AvatarFallback className="bg-primary/15 text-primary font-semibold text-xs">
                {initials(user?.name || user?.email)}
              </AvatarFallback>
            )}
          </Avatar>
        </button>
      </div>

      {/* Mobile nav strip */}
      <nav className="lg:hidden relative flex overflow-x-auto snap-x snap-mandatory gap-1 px-2 pb-2 border-t border-border scrollbar-thin">
        {flatNav.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => cn(
              "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs whitespace-nowrap snap-start shrink-0",
              isActive ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground"
            )}
          >
            <Icon size={14} weight="duotone" />
            {label}
          </NavLink>
        ))}

        {/* Sign out — the desktop sidebar carries this control but is
            hidden below lg, so mobile gets its own. Pinned to the right
            edge so it stays visible however far the strip is scrolled. */}
        <button
          type="button"
          onClick={onLogout}
          className="sticky right-0 ml-auto flex items-center gap-1.5 pl-3 pr-2 py-1.5 shrink-0 text-xs whitespace-nowrap bg-card border-l border-border text-muted-foreground hover:text-destructive transition-colors"
          data-testid="logout-btn-mobile"
          aria-label="Sign out"
        >
          <SignOut size={14} weight="duotone" />
          Sign out
        </button>
      </nav>
    </header>
  );
}

/**
 * iter-57 — Compact top-bar button used by both `display_qr` and
 * `scan_qr` additive-role entrypoints. Shows the label on desktop,
 * icon-only on narrow viewports so the top bar stays legible on
 * mobile even when a user holds both additive roles.
 */
function QRTopBarButton({ testId, onClick, icon, label, disabled, title }) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      title={title || label}
      className="gap-2 border-primary/40 text-primary hover:bg-primary/5 flex-none"
      data-testid={testId}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </Button>
  );
}
